/**
 * @author      hugo@zumper.com (Hugo Pineda)
 * @copyright   Copyright (c) 2022, Zumper
 */

import { extendTheme } from '@chakra-ui/react'
import type { StyleFunctionProps } from '@chakra-ui/theme-tools'
import { mode } from '@chakra-ui/theme-tools'
import { Z4 } from '@zcode-ui/themes'

import { colors } from './colors'
import {
  Z4Accordion,
  Z4Badge,
  Z4Button,
  Z4Checkbox,
  Z4Form,
  Z4FormError,
  Z4Heading,
  Z4Link,
  Z4Modal,
  Z4Popover,
  Z4Progress,
  Z4Radio,
  Z4Switch,
  Z4Tabs,
  Z4Text,
} from './componentStyles'
import { Z4Menu } from './componentStyles/menu'
import { bgDark, bgLight, colorDark, colorLight } from './constants'

const themeConfig = {
  ...Z4,
  colors: {
    ...Z4.colors,
    ...colors,
  },
  breakpoints: {
    base: '0px',
    xs: '360px',
    sm: '480px',
    md: '768px',
    desktop: '768px',
    lg: '992px',
    xl: '1280px',
    '2xl': '1440px',
  },
  config: {
    initialColorMode: 'light',
    useSystemColorMode: false,
  },
  components: {
    Accordion: Z4Accordion,
    Badge: Z4Badge,
    Button: Z4Button,
    Checkbox: Z4Checkbox,
    Form: Z4Form,
    FormError: Z4FormError,
    Heading: Z4Heading,
    Link: Z4Link,
    Menu: Z4Menu,
    Modal: Z4Modal,
    Popover: Z4Popover,
    Progress: Z4Progress,
    Radio: Z4Radio,
    Switch: Z4Switch,
    Text: Z4Text,
    Tabs: Z4Tabs,
  },
  fonts: {
    body: 'Moderat, "Moderat Fallback"',
    heading: 'Figtree, "Figtree Fallback"',
  },
  fontSizes: {
    xs: '0.75rem',
    sm: '0.875rem',
    md: '1rem',
    lg: '1.25rem',
    xl: '1.5rem',
    '2xl': '1.75rem',
    '3xl': '2rem',
    '4xl': '2.5rem',
    '5xl': '3rem',
    '6xl': '3.75rem',
    '7xl': '4.5rem',
    '8xl': '6rem',
    '9xl': '8rem',
  },
  fontWeights: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 650,
    extrabold: 700,
  },
  sizes: {
    container: {
      sm: '640px',
      md: '768px',
      lg: '1024px',
      xl: '1280px',
      xxl: '1440px',
    },
  },
  shadows: {
    outline: '0 0 0 4px var(--chakra-colors-neutral-200)',
  },
  styles: {
    global: (props: StyleFunctionProps) => ({
      a: {
        color: mode(colorLight, colorDark)(props),
        fontSize: 'md',
        textDecoration: 'underline',
      },
      body: {
        color: mode(colorLight, colorDark)(props),
        bg: mode(bgLight, bgDark)(props),
      },
      'h1, h2, h3, h4, h5, h6': {
        fontFamily: 'heading',
      },
      span: { fontFamily: 'body' },
      p: { fontFamily: 'body' },
      input: { fontFamily: 'body' },
      select: { fontFamily: 'body' },
      textarea: { fontFamily: 'body' },
    }),
  },
}

export type ChakraZ4Type = typeof themeConfig
export const ChakraZ4 = extendTheme(themeConfig)
